import {
  Flex,
  Text,
  Table,
  TableCellProps,
  Tbody,
  Td,
  Tr,
  Box,
  Spinner,
  Center,
  Button,
} from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";

import {
  CONNECT_WALLET_CTA,
  INITIAL_REWARS_DATA,
  REWARDS_DISTRIBUTION_DATA,
} from "@constants/common";
import { useCallback, useMemo } from "react";
import { numberToCurrency } from "utils";
import PointsDistributionHeader from "./Header";
import TagsRenderer from "./TagsRenderer";
import { useAccount } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";

interface props {
  dashboardData: typeof INITIAL_REWARS_DATA | null;
  isLoading: boolean;
}

interface ItemProps {
  row: (typeof REWARDS_DISTRIBUTION_DATA)[0];
  dashboardData: typeof INITIAL_REWARS_DATA | null;
}

interface TdProps extends TableCellProps {}

const TableCell = ({ children, ...props }: TdProps) => (
  <Td py={4} {...props}>
    {children}
  </Td>
);

const isTrueValue = (value: string | undefined) => {
  return value && Number(value);
};

const Item = ({ row, dashboardData }: ItemProps) => {
  const getValue = useCallback(
    (key: string | null, isComingSoon: boolean) => {
      if (key === null) return "-";

      if (
        dashboardData &&
        dashboardData[key as keyof typeof INITIAL_REWARS_DATA]
      ) {
        return numberToCurrency(
          (
            +dashboardData[key as keyof typeof INITIAL_REWARS_DATA] / 1000
          ).toFixed(4)
        );
      } else if (isComingSoon) {
        return (
          <Text fontWeight="500" fontSize="12px">
            {key}
          </Text>
        );
      }
      return "-";
    },
    [dashboardData]
  );

  return (
    <>
      {(row.kelpKey || row.elpKey) &&
      dashboardData &&
      (isTrueValue(
        dashboardData[row.kelpKey as keyof typeof INITIAL_REWARS_DATA]
      ) ||
        isTrueValue(
          dashboardData[row.elpKey as keyof typeof INITIAL_REWARS_DATA]
        )) ? (
        <Tr key={row.title}>
          <TableCell
            paddingLeft={0}
            // style={{ textWrap: "nowrap" }}
            paddingRight="4px"
          >
            <Flex gap="6px" flexDir="column" alignItems="flex-start">
              <Flex gap="4px">
                <Box
                  height="18px"
                  width="18px"
                  borderRadius="50%"
                  overflow="hidden"
                >
                  <BaseImage
                    height={18}
                    width={18}
                    alt="platform"
                    src={row.icon}
                    objectFit="cover"
                  />
                </Box>

                <Text fontWeight="500" fontSize="16px" flex="1">
                  {row.title}
                </Text>
              </Flex>
              <TagsRenderer tagsList={row.tags || []} />
            </Flex>
          </TableCell>
          <TableCell
            fontWeight="bold"
            textAlign="end"
            w="30%"
            paddingLeft={0}
            paddingRight="4px"
          >
            {getValue(row.kelpKey, row.isComingSoon)}
          </TableCell>
          {/* <TableCell fontWeight="bold" textAlign="end" w="30%" paddingEnd={0}>
            {getValue(row.elpKey, row.isComingSoon)}
          </TableCell> */}
        </Tr>
      ) : null}
    </>
  );
};

const PointsDistributionTable = ({ dashboardData, isLoading }: props) => {
  const { isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();

  const rowsCount = useMemo(() => {
    let count = 0;
    if (dashboardData) {
      REWARDS_DISTRIBUTION_DATA.map((row) => {
        if (
          dashboardData &&
          (isTrueValue(
            dashboardData[row.kelpKey as keyof typeof INITIAL_REWARS_DATA]
          ) ||
            isTrueValue(
              dashboardData[row.elpKey as keyof typeof INITIAL_REWARS_DATA]
            ))
        ) {
          count++;
        }
      });
    }
    return count;
  }, [dashboardData]);

  return (
    <Flex
      bg="white"
      p={{ base: "24px", md: "40px" }}
      gap="24px"
      borderRadius="24px"
      flexDir="column"
    >
      <Text
        color="textPrimary"
        fontSize="18px"
        lineHeight="24px"
        fontWeight="500"
      >
        Your rewards from DeFi
      </Text>

      <Table border="hidden" size="sm" variant="unstyled">
        <PointsDistributionHeader />
        <Tbody>
          {!isConnected ? (
            <Tr>
              <Td colSpan={3} py="32px">
                <Center>
                  <Button
                    borderRadius="12px"
                    bg="textPrimary"
                    p="16px 64px"
                    h="56px"
                    mt="12px"
                    width="100%"
                    _hover={{
                      bg: "textPrimary",
                    }}
                    onClick={() => openConnectModal?.()}
                  >
                    <Text
                      fontSize="16px"
                      lineHeight="24px"
                      fontWeight="600"
                      color="backgroundCard"
                    >
                      {CONNECT_WALLET_CTA}
                    </Text>
                  </Button>
                </Center>
              </Td>
            </Tr>
          ) : (
            <>
              {isLoading ? (
                <Tr>
                  <Td colSpan={3} py="32px">
                    <Center>
                      <Spinner size="lg" />
                    </Center>
                  </Td>
                </Tr>
              ) : (
                <>
                  {dashboardData && rowsCount > 0 ? (
                    <>
                      {REWARDS_DISTRIBUTION_DATA.map((row) => (
                        <Item
                          row={row}
                          dashboardData={dashboardData}
                          key={row.title}
                        />
                      ))}
                    </>
                  ) : (
                    <Tr>
                      <Td colSpan={3} py="32px">
                        <Center>-</Center>
                      </Td>
                    </Tr>
                  )}
                </>
              )}
            </>
          )}
        </Tbody>
      </Table>
    </Flex>
  );
};

export default PointsDistributionTable;
